/**
 * Settings → Organization → File activity (#118, Task B8).
 *
 * Who created / updated / moved / deleted / restored what, including scripts,
 * AI agents and system sweeps — a read-only view over the `file_audit` table
 * (B1-B7, already built and committed) through `GET /api/orgs/:id/file-audit`.
 * There is no write side; `view_file_audit` is a read-only capability.
 *
 * ── The gate, and the trap this file exists to avoid ─────────────────────
 *
 * `useMyCapabilities().can()` returns `false` for TWO different states —
 * "this caller is denied" and "the answer has not arrived yet" — and a page
 * that gates on `can()` alone therefore paints its refusal copy first and
 * corrects itself once the request lands. On a cold load that is a full
 * screen telling an owner they lack a permission they hold (see
 * `grant-loading-vs-denial.test.tsx`, and `SpendingPage`'s own docblock,
 * which this file follows exactly): loading is checked and rendered BEFORE
 * the denial branch, never folded into it.
 *
 * `view_file_audit` is owner/admin-only by default (`shared/types/src/
 * capabilities.ts`), so most members hitting this page directly (or via a
 * stale bookmark — the nav item itself is gated in `SettingsView`) will see
 * the resolved refusal, which is the correct, non-alarming answer for them.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useMyCapabilities } from '@papan/core-ui/ui'
import { PLATFORM_DATE_LOCALE } from '@papan/types'
import {
  fileAuditApi,
  type FileAuditRow,
  type FileAuditCursor,
  type FileAuditEventType,
  type FileAuditActorKind,
} from '../../client/file-audit'

const EVENT_TYPES: FileAuditEventType[] = ['created', 'updated', 'moved', 'deleted', 'restored']

/** Every non-`user` actor kind renders as a labeled badge rather than an
 *  email — the whole point of this column is telling a human apart from a
 *  script/agent/system actor AT A GLANCE, not just by reading the text. */
const ACTOR_KIND_BADGE: Record<Exclude<FileAuditActorKind, 'user' | 'share'>, string> = {
  script: 'themed-bg-tertiary themed-text-secondary',
  agent: 'bg-violet-500/15 text-violet-400',
  system: 'bg-amber-500/15 text-amber-400',
}

function ActorCell({ row }: { row: FileAuditRow }) {
  const { t } = useTranslation('core-ui')
  if (row.actorKind === 'user') {
    // A human actor — rendered as plain text, distinct from the badge
    // treatment every non-human actor gets below. Show the DISPLAY NAME, with
    // the full email on hover (a long email crowds the column). Fall back to
    // the email's local-part (`tap` from `tap@papan.local`) for a row written
    // before `actorName` was captured, then to the UUID — an opaque id still
    // beats no identity at all. `title` always carries the fullest identity we
    // have, so the hover is the email whenever there is one.
    const emailLocal = row.actorEmail ? row.actorEmail.split('@')[0] : null
    const label = row.actorName || emailLocal || row.actorUserId
    const tooltip = row.actorEmail ?? row.actorUserId ?? undefined
    return (
      // `block` so `truncate` (overflow-hidden + ellipsis + nowrap) actually
      // clips within the cell's max-width — an inline span never clips.
      <span className="themed-text truncate block" title={tooltip}>
        {label ?? t('settings.fileActivity.actorKind.user')}
        {/* Written through a connection (an AI app over MCP) acting as this member — the
            connection's name, with its id (the MCP grant id) on hover (0319). */}
        {row.connectionId && (
          <span
            className="block text-[11px] themed-text-muted truncate"
            title={t('settings.fileActivity.viaTitle', { id: row.connectionId })}
            data-testid="actor-via"
          >
            {t('settings.fileActivity.via', { name: row.connectionName || row.connectionId })}
          </span>
        )}
      </span>
    )
  }
  if (row.actorKind === 'share') {
    // #1263 — a change made through a share link, recorded as the link's audience allows (#776):
    // a public link's visitor is "Link visitor" with no email; a private / restricted link's is the
    // person (email on hover). The link is named by its id (hover) — never its token.
    const linkId = row.actorShareId?.split(':')[1] ?? null
    // No email ⇒ the link's anonymous visitor: the translated label (the stored name is the English one).
    const label = row.actorEmail ? row.actorName || row.actorEmail : t('settings.fileActivity.actorKind.share')
    return (
      <span className="themed-text truncate block" title={row.actorEmail ?? undefined} data-testid="actor-kind-share">
        {label}
        <span
          className="block text-[11px] themed-text-muted truncate"
          title={linkId ? t('settings.fileActivity.viaLinkTitle', { id: linkId }) : undefined}
          data-testid="actor-via-link"
        >
          {t('settings.fileActivity.viaLink')}
        </span>
      </span>
    )
  }
  return (
    <span
      data-testid={`actor-kind-${row.actorKind}`}
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${ACTOR_KIND_BADGE[row.actorKind]}`}
    >
      {t(`settings.fileActivity.actorKind.${row.actorKind}`)}
    </span>
  )
}

/** The bare file/folder NAME — the leaf of the resolved path — so a reader
 *  sees WHAT was acted on without parsing the whole path. A move preserves the
 *  name, so the current `path`'s leaf is correct there too; a delete already
 *  stores just the leaf (no live path to resolve). */
function fileLeafName(row: FileAuditRow): string {
  const p = row.path
  if (!p) return '—'
  const leaf = p.split('/').pop()
  return leaf && leaf.length > 0 ? leaf : p
}

function NameCell({ row }: { row: FileAuditRow }) {
  const name = fileLeafName(row)
  return (
    <span className="themed-text break-all" title={name === '—' ? undefined : name}>
      {name}
    </span>
  )
}

function PathCell({ row }: { row: FileAuditRow }) {
  const { t } = useTranslation('core-ui')
  // `break-all` (not `truncate`): a long path WRAPS onto further lines within
  // the cell's max-width rather than being clipped with an ellipsis. Paths are
  // a single unbroken token (no spaces), so plain word-wrap can't break them —
  // `break-all` lets the wrap fall anywhere. `title` still carries the full
  // string for a quick hover-read.
  if (row.eventType === 'moved' && row.oldPath) {
    return (
      <span className="themed-text-secondary break-all" title={`${row.oldPath} → ${row.path ?? ''}`}>
        {t('settings.fileActivity.movedPath', { from: row.oldPath, to: row.path ?? '' })}
      </span>
    )
  }
  return (
    <span className="themed-text-secondary break-all" title={row.path ?? undefined}>
      {row.path ?? '—'}
    </span>
  )
}

export function FileActivityPage({
  tenantId,
  userEmail,
}: {
  tenantId?: string
  userEmail?: string
}) {
  const { t } = useTranslation('core-ui')
  // `false` (never personal) — this page is org-only, mounted only inside
  // the `isOrg` block of `SettingsView` (see `ORG_ONLY_SETTINGS`). `loading`
  // is what separates "still fetching" from "denied" — see the file docblock.
  const { can, loading: capsLoading } = useMyCapabilities(tenantId, false, userEmail)
  const canRead = can('view_file_audit', 'read')

  const [actorInput, setActorInput] = useState('')
  const [actor, setActor] = useState('')
  // #580: filter by file NAME (matched against the resolved `path` column
  // via `?q=`) — a tenant admin knows names, not the internal file id.
  const [nameQueryInput, setNameQueryInput] = useState('')
  const [nameQuery, setNameQuery] = useState('')
  const [type, setType] = useState<FileAuditEventType | ''>('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const [rows, setRows] = useState<FileAuditRow[]>([])
  const [cursor, setCursor] = useState<FileAuditCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounce the free-text filters — same 300ms shape `AuditLog` already uses
  // for its actor filter, so a filter box on this settings surface doesn't
  // fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setActor(actorInput.trim()), 300)
    return () => clearTimeout(id)
  }, [actorInput])
  useEffect(() => {
    const id = setTimeout(() => setNameQuery(nameQueryInput.trim()), 300)
    return () => clearTimeout(id)
  }, [nameQueryInput])

  const filterArgs = useCallback(() => ({
    actor: actor || undefined,
    q: nameQuery || undefined,
    type: type || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
  }), [actor, nameQuery, type, from, to])

  const load = useCallback(async () => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    try {
      const res = await fileAuditApi.list(tenantId, filterArgs())
      setRows(res.rows)
      setCursor(res.nextCursor)
    } catch (e: any) {
      setError(e?.message || t('settings.fileActivity.error'))
    } finally {
      setLoading(false)
    }
    // `t` is deliberately NOT a dependency: `useTranslation()`'s mock (and
    // some real i18next configurations) can hand back a new function
    // identity on every render, and including it here re-fires this effect
    // on every render — an infinite fetch loop, not merely a lint nit. `t`
    // is still called correctly via closure; only re-running the FETCH on a
    // translation-identity change is what this avoids, and that was never
    // desired behavior anyway (a language switch should not re-query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, filterArgs])

  // Re-runs on every filter change (`load` closes over them via `filterArgs`)
  // — a fresh query always REPLACES `rows`, never appends, so changing a
  // filter mid-"Load more" can't leave stale pages from the old filter set
  // mixed in with the new one.
  useEffect(() => { if (canRead) load() }, [canRead, load])

  const loadMore = async () => {
    if (!tenantId || !cursor) return
    setLoadingMore(true)
    setError(null)
    try {
      const res = await fileAuditApi.list(tenantId, { ...filterArgs(), cursor })
      setRows((r) => [...r, ...res.rows])
      setCursor(res.nextCursor)
    } catch (e: any) {
      setError(e?.message || t('settings.fileActivity.error'))
    } finally {
      setLoadingMore(false)
    }
  }

  const clearFilters = () => {
    setActorInput('')
    setNameQueryInput('')
    setType('')
    setFrom('')
    setTo('')
  }
  const filtersActive = !!(actorInput || nameQueryInput || type || from || to)

  if (!tenantId) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noTenant')}</div>

  // The loading check runs BEFORE the denial check — see the file docblock.
  // Swapping this order is the exact regression `grant-loading-vs-denial.
  // test.tsx` exists to catch.
  if (capsLoading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>

  if (!canRead) {
    return (
      <div className="p-4 space-y-1">
        <p className="text-sm themed-text font-medium">{t('settings.fileActivity.title')}</p>
        <p className="text-xs themed-text-muted">{t('settings.fileActivity.noPermissionRead')}</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl">
      <h2 className="text-lg font-medium themed-text mb-1">{t('settings.fileActivity.title')}</h2>
      <p className="themed-text-muted text-xs mb-4">{t('settings.fileActivity.desc')}</p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">{t('settings.fileActivity.filters.actor')}</span>
          <input
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            placeholder={t('settings.fileActivity.filters.actorPh')}
            aria-label={t('settings.fileActivity.filters.actor')}
            className="themed-input border rounded px-2 py-1 text-xs w-48 outline-none focus:border-blue-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">{t('settings.fileActivity.filters.file')}</span>
          <input
            value={nameQueryInput}
            onChange={(e) => setNameQueryInput(e.target.value)}
            placeholder={t('settings.fileActivity.filters.filePh')}
            aria-label={t('settings.fileActivity.filters.file')}
            className="themed-input border rounded px-2 py-1 text-xs w-48 outline-none focus:border-blue-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">{t('settings.fileActivity.filters.type')}</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as FileAuditEventType | '')}
            aria-label={t('settings.fileActivity.filters.type')}
            className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
          >
            <option value="">{t('settings.fileActivity.filters.typeAll')}</option>
            {EVENT_TYPES.map((et) => (
              <option key={et} value={et}>{t(`settings.fileActivity.eventType.${et}`)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">{t('settings.fileActivity.filters.from')}</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label={t('settings.fileActivity.filters.from')}
            className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">{t('settings.fileActivity.filters.to')}</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label={t('settings.fileActivity.filters.to')}
            className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
          />
        </label>
        {filtersActive && (
          <button
            onClick={clearFilters}
            className="text-xs themed-text-muted hover:themed-text underline underline-offset-2 mb-1"
          >
            {t('settings.fileActivity.filters.clear')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3">
          <p className="text-sm text-red-300 flex-1">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
      ) : rows.length === 0 ? (
        <div className="themed-bg-secondary rounded-lg p-6 text-center">
          <p className="themed-text-muted text-sm">{t('settings.fileActivity.empty')}</p>
        </div>
      ) : (
        <div className="ws-drive-pill rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="themed-text-muted text-[11px] text-left border-b themed-border">
                <th className="px-3 py-2 font-medium">{t('settings.fileActivity.columns.actor')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.fileActivity.columns.event')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.fileActivity.columns.name')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.fileActivity.columns.path')}</th>
                <th className="px-3 py-2 font-medium">{t('settings.fileActivity.columns.when')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const when = new Date(row.eventAt)
                return (
                  <tr
                    key={row.id}
                    className="border-b themed-border last:border-b-0"
                  >
                    <td className="px-3 py-2 max-w-[220px]"><ActorCell row={row} /></td>
                    <td className="px-3 py-2 themed-text">{t(`settings.fileActivity.eventType.${row.eventType}`)}</td>
                    <td className="px-3 py-2 max-w-[200px]"><NameCell row={row} /></td>
                    <td className="px-3 py-2 max-w-[320px]"><PathCell row={row} /></td>
                    <td className="px-3 py-2 themed-text-muted text-xs whitespace-nowrap">
                      <time dateTime={row.eventAt} title={when.toISOString()}>
                        {when.toLocaleString(PLATFORM_DATE_LOCALE)}
                      </time>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {cursor && !loading && (
        <div className="mt-3">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {loadingMore ? t('common:loading') : t('settings.fileActivity.loadMore')}
          </button>
        </div>
      )}
    </div>
  )
}
